<script lang="ts" setup>
import { computed } from 'vue';
import { DateTime } from 'luxon';
import { FormBuilderContract } from '@/components/builder/form';
import { $t } from '@/plugins/localization';

const props = defineProps({
  "form": null,
  "blueprint": null,
  "entry": null,
  "rowIndex": null
});

const toDisplayText = (value: any): string =>
{
    if (value == null) return '';

    if (Array.isArray(value))
    {
        return value
            .map((item) =>
            {
                if (item == null) return '';

                if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') return String(item);

                if (typeof item === 'object')
                {
                    if ('text' in item && item.text)
                    {
                        if (typeof item.text === 'object')
                        {
                            return props.form.localization.translate(item.text);
                        }

                        return String(item.text);
                    }

                    if ('value' in item && item.value) return String(item.value);

                    if ('name' in item && item.name) return String(item.name);

                    if ('label' in item && item.label) return String(item.label);

                    if ('key' in item && item.key) return String(item.key);

                    if ('id' in item && item.id) return String(item.id);
                }

                return '';
            })
            .filter(Boolean)
            .join(', ');
    }

    if (typeof value === 'object')
    {
        if ('toFormat' in value && typeof value.toFormat === 'function')
        {
            return value.toFormat('yyyy-MM-dd HH:mm');
        }

        if ('text' in value && value.text)
        {
            if (typeof value.text === 'object')
            {
                return props.form.localization.translate(value.text);
            }

            return String(value.text);
        }

        if ('value' in value && value.value) return String(value.value);

        if ('name' in value && value.name) return String(value.name);

        if ('label' in value && value.label) return String(value.label);

        if ('key' in value && value.key) return String(value.key);

        return '';
    }

    return String(value);
};

const dateOutputFormat = computed(() =>
{
    return props.blueprint?.fieldType === 'Date'
        ? 'yyyy-MM-dd'
        : 'yyyy-MM-dd HH:mm';
});

const formatDateInLocalZone = (value: DateTime): string =>
{
    return value
        .setZone(DateTime.local().zoneName)
        .toFormat(dateOutputFormat.value);
};

const hasMeaningfulDefaultValue = (defaultValue: any): boolean =>
{
    if (defaultValue == null) return false;

    if (typeof defaultValue === 'string') return defaultValue.trim().length > 0;

    if (Array.isArray(defaultValue)) return defaultValue.length > 0;

    if (typeof defaultValue === 'object') return Object.keys(defaultValue).length > 0;

    return true;
};

const localizedBooleanText = (value: boolean): string =>
{
    const trueText = props.form.localization.translate(props.blueprint?.trueValueText);
    const falseText = props.form.localization.translate(props.blueprint?.falseValueText);

    if (value)
    {
        return trueText || $t('[[[Tak]]]');
    }

    return falseText || $t('[[[Nie]]]');
};

const numericPrecision = computed(() =>
{
    const isInteger = props.blueprint?.fieldType === 'Integer';

    return isInteger ? 0 : (props.blueprint?.precision ?? 0);
});

const isDecimalNumeric = computed(() =>
{
    return props.blueprint?.fieldType === 'Decimal' || props.blueprint?.fieldType === 'Percent';
});

const isNumericControl = computed(() =>
{
    return props.blueprint?.type === 'numeric' || props.blueprint?.type === 'logito-numeric';
});

const formatThousandsWithSpaces = (value: string): string =>
{
    const [integerPart, decimalPart] = value.split(',');
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return decimalPart != null ? `${groupedInteger},${decimalPart}` : groupedInteger;
};

const formatNumericValue = (value: number): string =>
{
    const fixed = value.toFixed(numericPrecision.value).replace('.', ',');
    const formattedNumber = isDecimalNumeric.value ? formatThousandsWithSpaces(fixed) : fixed;
    const prepend = props.blueprint?.affix?.prepend ?? '';
    const append = props.blueprint?.fieldType === 'Percent'
        ? '%'
        : (props.blueprint?.affix?.append ?? '');

    return `${prepend}${formattedNumber}${append}`;
};

const resolveDefaultValue = () =>
{
    if (props.blueprint?.type === 'logito-dictionary' && Array.isArray(props.entry?.defaultValue) && props.entry.defaultValue.length > 0)
    {
        return props.entry.defaultValue;
    }

    const defaultValue = props.blueprint?.defaultValue;

    if (typeof defaultValue === 'string')
    {
        return props.form.expressions.executeExpression(defaultValue, props.rowIndex);
    }

    return defaultValue;
};

const resolveDisplayValue = () =>
{
    const fieldValue = props.entry?.data;
    const readonly = props.form.expressions.readonly(props.blueprint, false, props.rowIndex);

    if (props.blueprint?.type === 'boolean')
    {
        return fieldValue ?? props.blueprint?.defaultValue ?? false;
    }

    const hasDefault = hasMeaningfulDefaultValue(props.blueprint?.defaultValue);

    return (fieldValue == null || (readonly && hasDefault))
        ? resolveDefaultValue()
        : fieldValue;
};

const toArray = (value: any): any[] =>
{
    if (Array.isArray(value)) return value;

    if (value == null) return [];

    return [value];
};

const displayValue = computed(() =>
{
    const resolved = resolveDisplayValue();

    if (props.blueprint?.type === 'boolean' && typeof resolved === 'boolean')
    {
        return localizedBooleanText(resolved);
    }

    if ((props.blueprint?.type === 'date' || props.blueprint?.type === 'logito-date') && DateTime.isDateTime(resolved))
    {
        return formatDateInLocalZone(resolved);
    }

    if ((props.blueprint?.type === 'numeric' || props.blueprint?.type === 'logito-numeric') && resolved != null)
    {
        const numericValue = typeof resolved === 'number' ? resolved : Number(resolved);

        if (!Number.isNaN(numericValue))
        {
            return formatNumericValue(numericValue);
        }
    }

    if (props.blueprint?.type === 'logito-choice')
    {
        const options = props.blueprint?.options ?? [];
        const selectedValues = toArray(resolved);

        return selectedValues
            .map((selectedValue: string) =>
            {
                const option = options.find((item: any) =>
                    item.key === selectedValue ||
                    item.value === selectedValue ||
                    `#additionalDataMode#${item.value}` === selectedValue
                );

                if (!option) return selectedValue;

                if (typeof option.text === 'object')
                {
                    return props.form.localization.translate(option.text);
                }

                return String(option.text ?? selectedValue);
            })
            .join(', ');
    }

    return toDisplayText(resolved);
});
</script>

<template>
    <div class="table-cell-display" :class="{ 'table-cell-display--numeric': isNumericControl }" :title="displayValue">
        {{ displayValue ?? '-' }}
    </div>
</template>

<style scoped lang="scss">
.table-cell-display {
    min-height: 38px;
    display: flex;
    align-items: flex-start;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.35;
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
}

.table-cell-display--numeric {
    justify-content: flex-end;
    text-align: right;
    width: 100%;
}
</style>
